import React, { createContext, useContext, useState, useEffect } from "react";
import {
  initializeSession,
  getSessionWithContact,
  updateSessionProgress,
} from "Lib/session-utils";

const SessionContext = createContext();

const SessionProvider = ({ children }) => {
  const [sessionCode, setSessionCode] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [isNewSession, setIsNewSession] = useState(false);
  const [isLoading, setIsLoading] = useState(
    () => !!new URLSearchParams(window.location.search).get("session"),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionCode = urlParams.get("session");

    if (urlSessionCode) {
      loadSession(urlSessionCode);
    }
  }, []);

  const loadSession = async (code) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getSessionWithContact(code);

      if (result.success) {
        setSessionCode(code);
        setSessionData(result.data);
        setContactId(result.data.contact_id);
      } else {
        setSessionCode(null);
        setError(result.error);
        console.error("[Session] Failed to load session:", result.error);
      }
    } catch (err) {
      setSessionCode(null);
      setError(err.message);
      console.error("[Session] Error loading session:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const createSession = async (contactData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await initializeSession(contactData);

      if (result.success) {
        const newSessionCode = result.sessionCode;
        setSessionCode(newSessionCode);
        setContactId(result.contactId);
        setSessionData(result.session);
        setIsNewSession(!result.isExisting);

        return {
          success: true,
          sessionCode: newSessionCode,
          contactId: result.contactId,
          isExisting: result.isExisting,
          currentPage: result.session?.current_page || 1,
        };
      } else {
        setError(result.error);
        console.error("[Session] Failed to create session:", result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMsg = err.message || "Unknown error";
      setError(errorMsg);
      console.error("[Session] Error creating session:", err);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const updateSession = async (updates, overrideSessionCode = null) => {
    const codeToUse = overrideSessionCode || sessionCode;

    if (!codeToUse) {
      console.error("[Session] No active session to update");
      return { success: false, error: "No active session" };
    }

    try {
      const result = await updateSessionProgress(codeToUse, updates);

      if (result.success) {
        setSessionData((prev) => ({
          ...prev,
          ...result.data,
        }));
      }

      return result;
    } catch (err) {
      console.error("[Session] Error updating session:", err);
      return { success: false, error: err.message };
    }
  };

  const clearSession = () => {
    setSessionCode(null);
    setContactId(null);
    setSessionData(null);
    setIsNewSession(false);
    setError(null);
  };

  const value = {
    sessionCode,
    contactId,
    sessionData,
    isNewSession,
    isLoading,
    setIsLoading,
    error,
    createSession,
    loadSession,
    updateSession,
    clearSession,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
};

export { SessionProvider, useSession, SessionContext };
